import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { formatDate } from '../../Utils/dateUtils';
import { FaArrowLeft, FaPrint, FaCheck } from 'react-icons/fa';

const BillSummaryReport = () => {
  const { division } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [company, setCompany] = useState('');
  const [companyDetails, setCompanyDetails] = useState({});
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBills, setSelectedBills] = useState([]);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const passedCompany = location.state?.company;
    const storedCompany = localStorage.getItem('selectedCompany');
    const resolvedCompany = passedCompany || storedCompany;

    if (!resolvedCompany) {
      alert('Company not found. Please login again.');
      navigate('/admin/dashboard');
      return;
    }

    setCompany(resolvedCompany.trim());
  }, [location.state, navigate]);

  useEffect(() => {
    if (!company || !division) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const billSummaryPath = `${company}/${division}/billSummary`;
        const companyPath = `companyDetails/${company}`;

        const [billSummarySnap, companySnap] = await Promise.all([
          get(ref(realtimeDb, billSummaryPath)),
          get(ref(realtimeDb, companyPath))
        ]);

        if (companySnap.exists()) {
          setCompanyDetails(companySnap.val());
        }

        if (billSummarySnap.exists()) {
          const billsData = [];
          billSummarySnap.forEach((bill) => {
            billsData.push({
              id: bill.key,
              billNumber: bill.val().billNumber,
              billDate: bill.val().billDate,
              grossTotal: bill.val().grossTotal,
              finalTotal: bill.val().finalTotal,
              workOrderNo: bill.val().workOrderNo
            });
          });
          setBills(billsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Failed to load bill data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company, division]);

  const toggleBillSelection = (billId) => {
    setSelectedBills(prev => 
      prev.includes(billId) 
        ? prev.filter(id => id !== billId) 
        : [...prev, billId]
    );
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Bill Summary Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .company-name { font-size: 18px; font-weight: bold; }
            .company-desc { font-size: 14px; }
            .company-address { font-size: 12px; margin-bottom: 10px; }
            .ref-line { margin: 10px 0; }
            .to-section { margin: 15px 0; }
            .subject-line { font-weight: bold; margin: 10px 0; }
            .reference-line { margin: 10px 0; }
            .content { margin: 15px 0; line-height: 1.5; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #000; padding: 8px; text-align: center; }
            th { background-color: #f2f2f2; }
            .signature { margin-top: 40px; text-align: right; }
            @media print {
              body { margin: 0; padding: 10mm; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${generateReportContent()}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 200);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const generateReportContent = () => {
    const selectedBillsData = bills.filter(bill => selectedBills.includes(bill.id));
    const totalTransformers = selectedBillsData.length * 5; // Assuming 5 transformers per bill
    const totalWithoutGST = selectedBillsData.reduce((sum, bill) => sum + (bill.grossTotal || 0), 0);
    const totalWithGST = selectedBillsData.reduce((sum, bill) => sum + (bill.finalTotal || 0), 0);

    return `
      <div class="header">
        <div class="company-name">${company}</div>
        <div class="company-desc">Transformer Service</div>
        <div class="company-address">Factory Address: ${companyDetails.address || 'Plot No.B-27, Jath Mini Industrial Area, Jath Dist-Sangli (9763661294)'}</div>
      </div>

      <div class="ref-line">
        REF-SPL/ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; DATE: ${formatDate(reportDate)}
      </div>

      <div class="to-section">
        TO,<br>
        THE EXECUTIVE ENGINEER<br>
        M.S.E.D.C.LTD.<br>
        ${division.toUpperCase()}-DIVISION<br>
        SANGLI
      </div>

      <div class="subject-line">
        SUB:- Submission of Bill For Repair Dist.Transformers.
      </div>

      <div class="reference-line">
        Ref:- Order No. ${selectedBillsData.map(bill => bill.workOrderNo).join(',')}
      </div>

      <div class="content">
        With reference to the above, we are glad to inform you that Dist.Transformers Repairs works
        Under ${division} Division is successfully done by us, according to work order. Bills in 2
        copies for the same are submitted here with along with Test Certificate as details below.
      </div>

      <table>
        <thead>
          <tr>
            <th>SR.NO.</th>
            <th>INVOICE NO</th>
            <th>NO OF TRANSFORMERS</th>
            <th>AMOUNT WITHOUT GST</th>
            <th>AMOUNT WITH GST 18%</th>
          </tr>
        </thead>
        <tbody>
          ${selectedBillsData.map((bill, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${bill.billNumber}</td>
              <td>5</td>
              <td>${bill.grossTotal.toFixed(2)}</td>
              <td>${bill.finalTotal.toFixed(2)}</td>
            </tr>
          `).join('')}
          <tr>
            <td colspan="2">TOTAL AMOUNT</td>
            <td>${totalTransformers}</td>
            <td>${totalWithoutGST.toFixed(2)}</td>
            <td>${totalWithGST.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <div class="signature">
        ${company}<br>
        PROPRIETOR
      </div>
    `;
  };

  const handleBack = () => {
    navigate(`/admin/division/${division}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="no-print flex justify-between items-center mb-6">
        <button
          onClick={handleBack}
          className="bg-gray-300 px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-400 transition"
        >
          <FaArrowLeft /> Back to Division
        </button>
        <button
          onClick={handlePrint}
          disabled={selectedBills.length === 0}
          className={`px-4 py-2 rounded flex items-center gap-2 transition ${
            selectedBills.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <FaPrint /> Print Report
        </button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold mb-4">Create Bill Summary Report</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Report Date</label>
          <input
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <h3 className="text-lg font-semibold mb-2">Select Bills to Include:</h3>
        <div className="max-h-96 overflow-y-auto border rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (₹)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Order</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bills.map((bill) => (
                <tr key={bill.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedBills.includes(bill.id)}
                      onChange={() => toggleBillSelection(bill.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{bill.billNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatDate(bill.billDate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{bill.finalTotal.toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{bill.workOrderNo || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedBills.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Preview</h3>
            <div className="border p-4 bg-white">
              <div dangerouslySetInnerHTML={{ __html: generateReportContent() }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillSummaryReport;